import { useParams } from '@tanstack/react-router';
import NotExists from '_/components/common/NotExists';
import { useAllOrganizations, useMyOrganizations, useOrgUsers } from '_/hooks/useOrganizations';
import { IOrganization, IOrgUser } from '_/types/organizations/organization.types';
import zeusSdk from '_/utils/SDKs';
import React, { createContext, useContext, ReactNode, useMemo, useEffect } from 'react';

import { useAuth } from './AuthContext';

interface OrganizationContextValue {
  organizations: IOrganization[];
  selectedOrganization: IOrganization | null;
  selectedOrgSlug: string | null;
  orgUsers: IOrgUser[];
  isLoading: boolean;
  isLoadingOrgUsers: boolean;
  isRefetchingOrgUsers: boolean;
  notFound: boolean;
  refetch: () => void;
  refetchOrgUsers: () => void;
}

export const OrganizationContext = createContext<OrganizationContextValue | undefined>(undefined);

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const params = useParams({ strict: false }) as Partial<{ orgSlug?: string }>;
  const orgSlug = params.orgSlug;
  const { isSuperAdmin } = useAuth();

  // Super admins see all orgs, regular users see only their orgs
  const { data: allOrganizations = [], isLoading: isLoadingAll, refetch: refetchAll } = useAllOrganizations(isSuperAdmin);

  const { data: myOrganizations = [], isLoading: isLoadingMy, refetch: refetchMy } = useMyOrganizations();

  const organizations: IOrganization[] = isSuperAdmin
    ? (allOrganizations as IOrganization[])
    : (myOrganizations as IOrganization[]);

  const isLoading = isSuperAdmin ? isLoadingAll : isLoadingMy;
  const refetch = isSuperAdmin ? refetchAll : refetchMy;

  const selectedOrganization = useMemo(() => {
    if (!orgSlug) return null;
    // Support both slug and ID lookup (ID is a UUID format)
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(orgSlug);
    return organizations.find((org) => (isUUID ? org.id === orgSlug : org.slug === orgSlug)) || null;
  }, [organizations, orgSlug]);

  // Fetch organization users when an org is selected
  const {
    data: orgUsers = [],
    isLoading: isLoadingOrgUsers,
    isRefetching: isRefetchingOrgUsers,
    refetch: refetchOrgUsers,
  } = useOrgUsers(selectedOrganization?.id);

  // Always use the actual slug, even if URL contains an ID
  const actualSlug = selectedOrganization ? selectedOrganization.slug : orgSlug;

  // Update SDK with current orgSlug
  useEffect(() => {
    if (actualSlug && actualSlug !== '_new') {
      zeusSdk.update({ orgSlug: actualSlug });
    }
  }, [actualSlug]);

  // Determine if org is not found: when loading is done, we have an orgSlug, but no matching organization
  const notFound = !isLoading && !!orgSlug && orgSlug !== '_new' && !selectedOrganization;

  const value: OrganizationContextValue = {
    organizations: organizations ?? [],
    selectedOrganization: selectedOrganization || null,
    selectedOrgSlug: actualSlug || null,
    orgUsers: orgUsers,
    isLoading: isLoading,
    isLoadingOrgUsers: isLoadingOrgUsers,
    isRefetchingOrgUsers: isRefetchingOrgUsers,
    notFound: notFound,
    refetch: refetch,
    refetchOrgUsers: refetchOrgUsers,
  };

  // If organization not found, render NotExists page
  if (notFound) {
    return <NotExists entityType="Organization" backUrl="/orgs" backLabel="Back to Organizations" />;
  }

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
}

export function useOrganization(): OrganizationContextValue {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
}
